/* eslint-disable react-hooks/rules-of-hooks -- pre-existing violations unmasked by #1772; tracked + to be fixed in #1842. Do not add new conditional hooks here. */
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  List,
  Typography,
  Switch,
  FormControlLabel,
  useTheme,
  CircularProgress,
} from '@mui/material'
import { useList, useOne } from '@refinedev/core'
import { Fragment, useEffect, useState } from 'react'

import ExpandMoreIcon from '@mui/icons-material/ExpandMore'
import ReactMarkdown from 'react-markdown'

import {
  describeProvenance,
  addedFieldName,
} from './revisionFormatting'

interface FunderRevisionProps {
  funderUUID: string
}
interface DiffData {
  values_changed?: Record<
    string,
    {
      diff: string
      old_value: any
      new_value: any
    }
  >
  dictionary_item_removed?: string[]
  dictionary_item_added?: Record<string, any>
  iterable_item_removed?: Record<string, any>
  iterable_item_added?: Record<string, any>
  type_changes?: Record<string, any>
}

const FUNDER_FIELD_NAME_MAP: Record<string, string> = {
  provenance: 'Provenance',
  uuid: 'UUID',
  name: 'Name',
  type: 'Type',
  website: 'Website',
  description: 'Description',
  notes: 'Notes',
  profile_embedding: 'Profile Embedding',
  profile_text: 'Profile Text',
  region_ids: 'Locations',
  active: 'Active',
  status: 'Status',
  flag_for_updates: 'Flag for Updates',
  // mapping for new opportunity fields
  fund_name: 'Fund Name',
  is_relational: 'Is Relational',
  always_open: 'Always Open',
  criteria: 'Criteria',
  exclusions: 'Exclusions',
  timing: 'Timing',
  amount: 'Amount',
  questions: 'Questions',
  funder_uuid: 'Funder',
  draft_profile_text: 'Draft Profile Text',
  application_info: 'Application Info',
  references: 'References',
  updated_by: 'Updated By',
  next_check_in_date: 'Next Check In Date',
  only_funds_registered_charities: 'Only Funds Registered Charities',
  gambling_proceeds: 'Gambling Proceeds',
  is_international: 'Is International',
  kaupapa_maori: 'Kaupapa Māori',
  iwi: 'Iwi',
  email_address: 'Email Address',
}

function translateFieldName(fieldPath: string): string {
  const fieldName = fieldPath.includes("root['")
    ? fieldPath.split("root['")[1]?.split("']")[0]
    : fieldPath

  const fieldMap = FUNDER_FIELD_NAME_MAP
  return fieldMap[fieldName] || fieldName
}

function getRegionName(regionId: number) {
  const { data: regionData } = useOne({
    resource: 'regions',
    id: regionId,
    errorNotification: (error, values, resource) => {
      if (error?.response?.status === 404) {
        return false // Suppress 404 error for deleted regions
      }
      return {
        message: 'Failed to load region',
        description: error?.message || `Error loading region ${regionId}`,
        type: 'error',
      }
    },
  })
  return regionData?.data?.name || 'Unknown Location (Deleted)'
}

function getIwiName(iwiId: number) {
  const { data: iwiData } = useOne({
    resource: 'iwi',
    id: iwiId,
    errorNotification: (error, values, resource) => {
      if (error?.response?.status === 404) {
        return false // Suppress 404 error for deleted iwi
      }
      return {
        message: 'Failed to load iwi',
        description: error?.message || `Error loading iwi ${iwiId}`,
        type: 'error',
      }
    },
  })
  return iwiData?.data?.name || 'Unknown Iwi (Deleted)'
}

function getOpportunityDetails(opportunityUUID: string) {
  const { data: opportunityData } = useOne({
    resource: 'opportunities',
    id: opportunityUUID,
    errorNotification: (error, values, resource) => {
      return {
        message: 'Failed to load opportunity',
        description:
          error?.message || `Error loading opportunity ${opportunityUUID}`,
        type: 'error',
      }
    },
  })

  // Add fallback values for when data fetch fails
  return {
    uuid: opportunityData?.data?.uuid || opportunityUUID,
    fund_name: opportunityData?.data?.fund_name || 'Unknown Fund',
    is_relational: opportunityData?.data?.is_relational ? 'True' : 'False',
    always_open: opportunityData?.data?.always_open ? 'True' : 'False',
    description:
      opportunityData?.data?.description || 'No description available',
    criteria: opportunityData?.data?.criteria || 'No criteria available',
    exclusions: opportunityData?.data?.exclusions || 'No exclusions available',
    timing: opportunityData?.data?.timing || 'No timing information',
    amount: opportunityData?.data?.amount || 'Amount not specified',
    questions: opportunityData?.data?.questions || 'No questions available',
    notes: opportunityData?.data?.notes || 'No notes available',
  }
}

export default function FunderRevisions({ funderUUID }: FunderRevisionProps) {
  const [loading, setLoading] = useState(true)

  const { data: funderRevisionData, isLoading } = useList({
    resource: 'funder-revisions',
    meta: {
      funder_uuid: funderUUID,
    },
    errorNotification: (error, values, resource) => {
      return {
        message: `Funder Revisions Not Found`,
        description: error?.message || `Error loading revisions for funder`,
        type: 'error',
      }
    },
  })

  // debounce to avoid rendering data too quickly
  useEffect(() => {
    const timer = setTimeout(() => {
      setLoading(false)
    }, 500)

    return () => clearTimeout(timer)
  }, [funderUUID])

  if (isLoading || loading) {
    return (
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          height: '50vh',
        }}
      >
        <CircularProgress />
      </Box>
    )
  }

  if (!funderRevisionData || !Object.keys(funderRevisionData).length) {
    return (
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
        }}
      >
        <Typography variant="h5" sx={{ textAlign: 'center' }}>
          Revisions
        </Typography>
        <Typography variant="body2" sx={{ textAlign: 'center' }}>
          No revision data found
        </Typography>
      </Box>
    )
  }

  return (
    <>
      <Box padding={2}>
        <Typography variant="h4" gutterBottom>
          Revisions
        </Typography>
        <List>
          {Object.entries(funderRevisionData).map(([name, versions]) => (
            <RevisionsExpandable key={name} name={name} versions={versions} />
          ))}
        </List>
      </Box>
    </>
  )
}

function RevisionsExpandable({
  name,
  versions,
}: {
  name: string
  versions: any[]
}) {
  if (!versions || versions.length === 0) return null

  const sortedVersions = Object.values(versions).sort(
    (a, b) => b.version - a.version
  )

  if (sortedVersions.length === 0) return null

  return (
    <Box>
      <Typography variant="h5" sx={{ my: 1 }}>
        Latest Version
      </Typography>
      <VersionDetails version={sortedVersions[0]} />
      {sortedVersions.length > 1 && (
        <Box marginTop={5}>
          <Box>
            <Typography variant="h5" sx={{ my: 1 }}>
              Previous Versions
            </Typography>
          </Box>

          {sortedVersions.slice(1).map((version) => (
            <VersionDetails key={version.version} version={version} />
          ))}
        </Box>
      )}
    </Box>
  )
}

function VersionDetails({ version }: { version: any }) {
  if (version == null || version.diff == null) {
    return null
  }

  const diffData = version.diff

  return (
    <Accordion>
      <AccordionSummary expandIcon={<ExpandMoreIcon />}>
        <Typography sx={{ width: '33%', flexShrink: 0, fontWeight: 'bold' }}>
          Version {version.version}
        </Typography>
        <Typography sx={{ color: 'text.secondary' }}>
          {new Date(version.date).toLocaleString('en-NZ', { hour12: true })}
        </Typography>
      </AccordionSummary>

      <AccordionDetails>
        <Typography variant="subtitle1">
          <strong>Data Entry By:</strong> {version.data_entry_by}
        </Typography>
        <Typography variant="subtitle1" marginBottom={3}>
          <strong>Date:</strong>{' '}
          {new Date(version.date).toLocaleString('en-NZ', { hour12: true })}
        </Typography>
        <DiffSection diffData={diffData as DiffData} data={version.data} />
      </AccordionDetails>
    </Accordion>
  )
}

function renderUnifiedDiff(diff: string, theme: 'light' | 'dark') {
  const colours = {
    light: {
      background: '#f5f5f5',
      text: '#1e1e1e',
      added: '#4caf50',
      removed: '#e57373',
      context: '#616161',
    },
    dark: {
      background: '#1e1e1e',
      text: '#f5f5f5',
      added: '#81c784',
      removed: '#ef5350',
      context: '#bdbdbd',
    },
  }

  const themeColours = colours[theme]

  return (
    <Box
      component="pre"
      sx={{
        background: themeColours.background,
        color: themeColours.text,
        padding: '10px',
        borderRadius: '4px',
        fontFamily: 'monospace',
        overflowX: 'wrap',
        whiteSpace: 'pre-wrap',
      }}
    >
      {diff.split('\n').map((line, index) => (
        <Typography
          key={index}
          component="span"
          sx={{
            display: 'block',
            color: line.startsWith('-')
              ? themeColours.removed
              : line.startsWith('+')
                ? themeColours.added
                : themeColours.context,
          }}
        >
          {line}
        </Typography>
      ))}
    </Box>
  )
}

function DiffSection({ diffData, data }: { diffData: DiffData; data?: any }) {
  if (!diffData) return null

  const [showDiff, setShowDiff] = useState(false)

  const theme = useTheme()
  const lightOrDarkMode = theme.palette.mode

  function handleToggleDiff() {
    setShowDiff(!showDiff)
  }

  return (
    <Box marginBottom={4}>
      {/* Added or Changed Types (eg. New or removed db columns) */}
      {diffData.type_changes && (
        <Box marginBottom={2}>
          <Typography variant="h6" sx={{ mb: 1, textDecoration: 'underline' }}>
            New Field Added or Removed
          </Typography>
          {Object.entries(diffData.type_changes).map(([key, value], index) =>
            value.old_type == "<class 'NoneType'>" ? (
              <Fragment key={`type-added-change-${index}-${key}`}>
                <Typography
                  variant="h6"
                  sx={{ mb: 1, textDecoration: 'underline' }}
                >
                  New Field Added
                </Typography>
                <Typography>
                  <strong>{translateFieldName(key)}</strong> field was added
                  with value:{' '}
                  {key.includes('iwi')
                    ? value.new_value
                        .map((id: any) => getIwiName(id))
                        .join(', ')
                    : JSON.stringify(value.new_value)}
                </Typography>
              </Fragment>
            ) : value.new_type == "<class 'NoneType'>" ? (
              <Fragment key={`type-removed-change-${index}-${key}`}>
                <Typography
                  variant="h6"
                  sx={{ mb: 1, textDecoration: 'underline' }}
                >
                  New Field Removed
                </Typography>
                <Typography>
                  <strong>{translateFieldName(key)}</strong> field was removed
                  with value: {JSON.stringify(value.old_value)}
                </Typography>
              </Fragment>
            ) : null
          )}
        </Box>
      )}
      {/* Removed Items */}
      {diffData.dictionary_item_removed &&
        diffData.dictionary_item_removed.length > 0 && (
          <Box marginBottom={2}>
            <Typography
              variant="h6"
              sx={{ mb: 1, textDecoration: 'underline' }}
            >
              Removed Items
            </Typography>
            {diffData.dictionary_item_removed?.map(
              (item: string, index: number) => (
                <Typography key={`removed-item-${index}-${item}`}>
                  <strong>{translateFieldName(item)}</strong> field was removed.
                </Typography>
              )
            )}
          </Box>
        )}

      {/* Added Items */}
      {diffData.dictionary_item_added && (
        <Box marginBottom={2}>
          <Typography variant="h6" sx={{ mb: 1, textDecoration: 'underline' }}>
            Added Items
          </Typography>
          {(Array.isArray(diffData.dictionary_item_added)
            ? diffData.dictionary_item_added
            : Object.values(diffData.dictionary_item_added)
          ).map((path: unknown, index: number) => {
            // GH #1846 — entries are DeepDiff PATH STRINGS, not values, so the
            // actual value has to come from the revision snapshot. `provenance`
            // (the why/who of a direct write) renders as labelled rows;
            // everything else keeps the generic JSON fallback.
            const field = addedFieldName(path)
            const added = field ? data?.[field] : undefined
            const provenanceRows =
              field === 'provenance' ? describeProvenance(added) : null
            return (
              <Box key={`added-item-${index}-${String(path)}`} sx={{ mb: 1 }}>
                <Typography>
                  <strong>{translateFieldName(String(path))}</strong>
                  {provenanceRows
                    ? ''
                    : ` field was added with value: ${JSON.stringify(added)}`}
                </Typography>
                {provenanceRows && (
                  <Box sx={{ pl: 2 }}>
                    {provenanceRows.map((row) => (
                      <Typography key={row.label} variant="body2">
                        <strong>{row.label}:</strong> {row.value}
                      </Typography>
                    ))}
                  </Box>
                )}
              </Box>
            )
          })}
        </Box>
      )}

      {/* Values Changed */}
      {diffData.values_changed && (
        <Box marginBottom={2}>
          {Object.entries(diffData.values_changed).map(
            ([key, change], index) => (
              <Box key={index + key} marginBottom={2}>
                {key === 'root' ? (
                  <Box
                    key={`changed-item-${key}-${index}-${change}`}
                    marginBottom={2}
                  >
                    <Typography variant="h6" sx={{ mb: 2 }}>
                      <strong>New Entry</strong>
                    </Typography>
                    <Box
                      bgcolor={lightOrDarkMode === 'light' ? '#f5f5f5' : '#333'}
                      borderRadius={1}
                      padding={2}
                      mb={2}
                    >
                      {Object.entries(change.new_value || {}).map(
                        ([field, value], fieldIndex) => (
                          <Box key={fieldIndex} sx={{ mb: 1 }}>
                            {field === 'region_ids' ? (
                              <>
                                <Typography variant="body2">
                                  <strong>{translateFieldName(field)}</strong>
                                </Typography>
                                <Typography variant="body1">
                                  {Array.isArray(value)
                                    ? value.map((regionId: number) =>
                                      getRegionName(regionId)
                                    )
                                    : 'None'}
                                </Typography>
                              </>
                            ) : field === 'iwi' ? (
                              <>
                                <Typography variant="body2">
                                  <strong>{translateFieldName(field)}</strong>
                                </Typography>
                                <Typography variant="body1">
                                  {Array.isArray(value)
                                    ? value.map((iwiId: number) =>
                                        getIwiName(iwiId)
                                      )
                                    : 'None'}
                                </Typography>
                              </>
                            ) : (
                              <>
                                <Typography variant="body2">
                                  <strong>{translateFieldName(field)}</strong>
                                </Typography>
                                {String(value) !== 'null' ? (
                                  <ReactMarkdown
                                    className={`markdown-${lightOrDarkMode}`}
                                  >
                                    {String(value)}
                                  </ReactMarkdown>
                                ) : (
                                  'None'
                                )}
                              </>
                            )}
                          </Box>
                        )
                      )}
                    </Box>
                  </Box>
                ) : (
                  <>
                    <Typography
                      variant="h6"
                      sx={{ mb: 1, textDecoration: 'underline' }}
                    >
                      Changed Values
                    </Typography>
                    <Typography variant="body1" sx={{ mb: 2 }}>
                      <strong>{translateFieldName(key)}</strong> field was
                      changed:
                    </Typography>
                    <Typography variant="body2">
                      <strong>New Value</strong>
                    </Typography>
                    <Box borderRadius={1} paddingX={2} paddingY={1} mb={2}>
                      {typeof change.new_value === 'string' ? (
                        <ReactMarkdown
                          className={`markdown-${lightOrDarkMode}`}
                        >
                          {change.new_value}
                        </ReactMarkdown>
                      ) : typeof change.new_value === 'boolean' &&
                        change.new_value === true ? (
                        'Yes'
                      ) : (
                        'No'
                      )}
                    </Box>
                    <Typography variant="body2">
                      <strong>Old Value</strong>
                    </Typography>
                    <Box borderRadius={1} paddingX={2} paddingY={1} mb={2}>
                      {typeof change.old_value === 'string' ? (
                        <ReactMarkdown
                          className={`markdown-${lightOrDarkMode}`}
                        >
                          {change.old_value}
                        </ReactMarkdown>
                      ) : typeof change.old_value === 'boolean' &&
                        change.old_value === true ? (
                        'Yes'
                      ) : (
                        'No'
                      )}
                    </Box>
                    {change.diff && (
                      <FormControlLabel
                        control={<Switch onChange={handleToggleDiff} />}
                        label="Show Diff View"
                      />
                    )}
                  </>
                )}

                {change.diff &&
                  showDiff &&
                  renderUnifiedDiff(change.diff, lightOrDarkMode)}
              </Box>
            )
          )}
        </Box>
      )}

      {/* Removed Iterable Items */}
      {diffData.iterable_item_removed && (
        <Box marginBottom={2}>
          <Typography variant="h6" sx={{ mb: 1, textDecoration: 'underline' }}>
            Removed Items
          </Typography>
          {Object.entries(diffData.iterable_item_removed).map(
            ([key, value], index) =>
              key.includes("root['region_ids']") ? (
                <Typography key={`region-${key}-${index}`}>
                  Location was deleted with value: {getRegionName(value)}
                </Typography>
              ) : key.includes("root['iwi']") ? (
                <Typography key={`iwi-${key}-${index}`}>
                  Iwi was deleted with value: {getIwiName(value)}
                </Typography>
              ) : key.includes("root['opportunity_ids']") ? (
                <Box key={`opp-${key}-${index}`}>
                  {typeof value === 'object' && (
                    <>
                      <strong>Opportunity was deleted</strong>

                      {Object.entries(value).map(([key, val]) => (
                        <Typography
                          key={`opp-${key}-${index}-${val}`}
                          variant="body2"
                        >
                          <strong>{translateFieldName(key)}:</strong>
                          {val as string}
                        </Typography>
                      ))}
                    </>
                  )}
                </Box>
              ) : (
                <Typography key={index + key}>
                  <strong>{translateFieldName(key)}</strong> field was added
                  with value: {JSON.stringify(value)}
                </Typography>
              )
          )}
        </Box>
      )}

      {/* Added Iterable Items ie. Locations */}
      {diffData.iterable_item_added && (
        <Box marginBottom={2}>
          <Typography variant="h6" sx={{ mb: 1, textDecoration: 'underline' }}>
            Added Items
          </Typography>
          {Object.entries(diffData.iterable_item_added).map(
            ([key, value], index) =>
              key.includes("root['region_ids']") ? (
                <Typography key={`region-${index}-${key}-${value}`}>
                  New location was added with value: {getRegionName(value)}
                </Typography>
              ) : key.includes("root['iwi']") ? (
                <Typography key={`iwi-${index}-${key}-${value}`}>
                  New iwi was added with value: {getIwiName(value)}
                </Typography>
              ) : key.includes("root['opportunity_ids']") ? (
                <Box key={`opp-${index}-${key}-${value}`}>
                  <strong>New Opportunity was added:</strong>
                  {Object.entries(getOpportunityDetails(value)).map(
                    ([key, value]) => (
                      <Typography
                        key={`opp-${key}-${index}-${value}`}
                        variant="body2"
                      >
                        <strong>{translateFieldName(key)}:</strong>{' '}
                        {value || 'None'}
                      </Typography>
                    )
                  )}
                </Box>
              ) : (
                <Typography key={index + key + value}>
                  <strong>{translateFieldName(key)}</strong> field was added
                  with value: {JSON.stringify(value)}
                </Typography>
              )
          )}
        </Box>
      )}
    </Box>
  )
}
